import React, { useState, useEffect, useRef, useMemo, Fragment, useCallback } from 'react';
import { basedAxios as Axios } from '../../store/Store';
import ArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import { observer } from 'mobx-react-lite';
import { Combobox } from '../../components/tempImports/Combobox';
import { Transition } from '../../components/tempImports/Transition';
import SystemLabel from '../../components/Systemlabel/SystemLabel';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import { DROPDOWN_OPTION, useDropdownClass } from '../../components/Dropdown/dropdown';

const includes = (source, target) => {
  if (!source || !target) return false;
  return source?.toLowerCase()?.includes(target.toLowerCase());
};

export const CountrySelect = observer(props => {
  const dropdownClass = useDropdownClass();
  const containerRef = useRef(null);
  const [countries, setCountries] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);

  const {
    lang,
    eventId,
    selectGrid,
    countryGroup = '1',
    value,
    disabled,
    onChange,
    readOnly,
    useCode = false,
    countryCode,
    loaded,
    valid = undefined,
    preselected,
    style = {},
  } = props;

  useOnClickOutside([{ current: containerRef.current }], () => setOpen(false), open);

  const getCountries = useCallback(async () => {
    try {
      const requestOptions = {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      };
      const { data } = await Axios.get(`./country/getselection/${eventId}/${lang}`, requestOptions);

      let countryList = data.map((c, index) => ({
        value: c.countryId,
        label: lang == 'swe' || lang === undefined ? c.nameSwe : c.nameInt,
        europeCountry: c.europeCountry === null ? false : c.europeCountry,
        nordicCountry: c.nordicCountry === null ? false : c.nordicCountry,
        swedishCountry: c.nameInt == 'Sweden' ? true : false,
        countryCode: c.code,
        name: c.nameInt,
      }));

      if (countryGroup === '2') {
        countryList = countryList.filter(c => c.nordicCountry === true);
      }

      if (countryGroup === '3') {
        countryList = countryList.filter(c => c.europeCountry === true);
      }

      setCountries([
        {
          value: 0,
          label: '',
          europeCountry: null,
          nordicCountry: null,
          countryId: 0,
        },
        ...countryList,
      ]);
    } catch (error) {
      console.error(error);
    }
  }, [lang, eventId]);

  useEffect(() => {
    if (lang && eventId) {
      getCountries();
    }
  }, [lang, eventId, getCountries]);

  const filteredCountries = useMemo(() => {
    if (query === '') return countries;
    return countries.filter(({ label }) => includes(label, query));
  }, [selectGrid, countries, query]);

  const selectedCountry = useMemo(() => {
    return countries?.find(({ value: _value }) => _value === value);
  }, [countries, value]);

  const handleChange = useCallback(
    country => {
      onChange(country.value, country, false);
      setOpen(false);
    },
    [onChange]
  );

  const onInput = useCallback(
    event => {
      const { value } = event.target;
      setQuery(value);
      setOpen(!!value);
    },
    [onChange]
  );

  return (
    <div className="select-wrap selector" ref={containerRef}>
      <Combobox disabled={disabled || readOnly} value={selectedCountry ?? {}} onChange={handleChange}>
        <div className="autocomplete">
          <div className="input-base">
            <Combobox.Input
              onClick={() => setOpen(prev => !prev)}
              onChange={onInput}
              displayValue={props => props?.label ?? ''}
              className="cms-field person form-control"
              style={{ marginLeft: 0, ...style, width: '100%', ...(valid === false ? { border: '1px solid #d53443' } : {}) }}
            />
            <Combobox.Button onClick={() => setOpen(prev => !prev)}>
              {(!readOnly && !disabled) && (
                <ArrowDownIcon />
              )}
            </Combobox.Button>
          </div>
          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery('')}
          >
            <Combobox.Options className={dropdownClass}>
              {filteredCountries?.length === 0 ? (
                <div style={{ padding: 10 }}>
                  <SystemLabel name="NoResult" />
                </div>
              ) : (
                filteredCountries?.map(country => (
                  <Combobox.Option key={country.value} value={country} as={Fragment}>
                    {({ selected }) => <li className={`${DROPDOWN_OPTION}${selected ? ' selected' : ''}`}>{country.label}</li>}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
});
